import React, { useState, useRef, useEffect } from "react";
import { Typography, TextField, IconButton, Box } from "@mui/material";
import { Edit, Check, Close } from "@mui/icons-material";

export const EditableTitle = ({ title, onSave, variant = "h5" }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [isSaving, setIsSaving] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (!editedTitle.trim()) {
      setEditedTitle(title);
      setIsEditing(false);
      return;
    }

    if (editedTitle === title) {
      setIsEditing(false);
      return;
    }

    setIsSaving(true);
    const success = await onSave(editedTitle.trim());
    setIsSaving(false);

    if (success) {
      setIsEditing(false);
    } else {
      setEditedTitle(title);
    }
  };

  const handleCancel = () => {
    setEditedTitle(title);
    setIsEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSave();
    } else if (e.key === "Escape") {
      handleCancel();
    }
  };

  if (isEditing) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <TextField
          inputRef={inputRef}
          value={editedTitle}
          onChange={(e) => setEditedTitle(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleSave}
          disabled={isSaving}
          size="small"
          fullWidth
          inputProps={{ maxLength: 200 }}
          sx={{ flex: 1 }}
        />
        <IconButton
          size="small"
          onClick={handleSave}
          disabled={isSaving}
          color="primary"
        >
          <Check />
        </IconButton>
        <IconButton size="small" onClick={handleCancel} disabled={isSaving}>
          <Close />
        </IconButton>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        cursor: "pointer",
        "&:hover": {
          "& .edit-icon": {
            color: "primary.main",
          },
        },
      }}
      onClick={() => setIsEditing(true)}
    >
      <Typography variant={variant}>
        {title || "Untitled Conversation"}
      </Typography>
      <Edit
        className="edit-icon"
        sx={{
          fontSize: "1.25rem",
          color: "text.secondary",
          transition: "color 0.2s",
        }}
      />
    </Box>
  );
};
